import { lazy } from 'react';
import { createBrowserRouter, Outlet, useLocation } from 'react-router-dom';
import { EditorHeader } from '../components';
import { TourProvider } from '../components/Onboarding';
import { withSuspense } from '../utils/withSuspense';
import { IS_DEV } from '@/constants/constants';
import { isPublicSigningPath } from '@/utils/public-routes';

const Authenticate = lazy(() => import('../pages/Authenticate'));
const Authorize = lazy(() => import('../pages/Authorize'));
const PDFEditorOld = lazy(() => import('../pages/PDFEditor'));
const PDFEditor = lazy(() => import('../pages/Envelope/PDFEditor'));
const SignPDF = lazy(() => import('../pages/SignPDF'));
const PDFPreview = lazy(() => import('../pages/Envelope/PDFPreview'));
const Download = lazy(() => import('../pages/Download'));
const MultipleFilesDownload = lazy(() => import('../pages/Envelope/Download'));
const VerifyEmail = lazy(() => import('../pages/VerifyEmail'));
const PDFPreviewOld = lazy(() => import('../pages/PDFPreview'));
const Reauthenticate = lazy(() => import('../pages/Reauthenticate'));
const GeneratePDF = lazy(() => import('../pages/GeneratePDF/GeneratePDF'));
const ShortLink = lazy(() => import('../pages/ShortLink'));
const PaymentSuccessful = lazy(() =>
  import('../pages/Stripe/PaymentSuccessful'),
);
const StripeConnectionSuccessful = lazy(() =>
  import('../pages/Stripe/StripeConnectionSuccessful'),
);
const IntegrationErrorPage = lazy(() =>
  import('../components/IntegrationErrorPage/IntegrationErrorPage'),
);
const TemplateEditor = lazy(() => import('../pages/Envelope/TemplateEditor'));
const SuperDocEditorV3 = lazy(() => import('../pages/V3/SuperDocEditor'));
const PDFEditorV3 = lazy(() => import('../pages/V3/PDFEditor'));
const SignDocs = lazy(() => import('../pages/V3/SignDocs'));
const PDFPreviewV3 = lazy(() => import('../pages/V3/PDFPreviewV3'));
const ShortLinkV3 = lazy(() => import('../pages/V3/ShortLinkV3'));
const TemplateEditorV3 = lazy(() => import('../pages/V3/TemplateEditor'));
const DownloadV3 = lazy(() => import('../pages/V3/Download'));
const FormEditor = lazy(() => import('../pages/V3/FormEditor'));

// Dev-only pages
const IconGallery = lazy(() => import('../pages/Dev/IconGallery'));

// Superadmin portal (internal)
const SuperAdminLayout = lazy(() =>
  import('../pages/SuperAdmin/SuperAdminLayout'),
);
const SuperAdminAccountsList = lazy(() =>
  import('../pages/SuperAdmin/AccountsList'),
);
const SuperAdminAccountDetail = lazy(() =>
  import('../pages/SuperAdmin/AccountDetail'),
);
const SuperAdminBoardDetail = lazy(() =>
  import('../pages/SuperAdmin/BoardDetail'),
);

const AppRoutes = () => {
  const { pathname } = useLocation();
  const outlet = <Outlet />;

  if (isPublicSigningPath(pathname) || pathname.startsWith('/superadmin')) {
    return outlet;
  }

  return <TourProvider>{outlet}</TourProvider>;
};

const routes = [
  {
    path: '/authenticate',
    element: withSuspense(<Authenticate />),
  },
  {
    path: '/re-authenticate',
    element: withSuspense(<Reauthenticate />),
  },
  {
    path: '/stripe/payment-successful',
    element: withSuspense(<PaymentSuccessful />),
  },
  {
    path: '/stripe/return',
    element: withSuspense(<StripeConnectionSuccessful />),
  },
  {
    path: '/sign/:shortId',
    element: withSuspense(<ShortLinkV3 />),
  },
  {
    path: '/s/:shortId',
    element: withSuspense(<ShortLink />),
  },
  {
    path: '/authorize',
    element: withSuspense(<Authorize />),
  },
  {
    path: '/sign/:itemId/:id',
    element: withSuspense(<SignPDF />),
  },
  {
    path: '/verify-email/:token',
    element: withSuspense(<VerifyEmail />),
  },
  {
    path: '/generate-pdf/:id/:itemId',
    element: withSuspense(<GeneratePDF />),
  },
  {
    path: '/download/:fileHistoryId/:fileId',
    element: withSuspense(<MultipleFilesDownload />),
  },
  {
    path: '/download/:id',
    element: withSuspense(<Download />),
  },
  {
    path: '/file-preview/:itemId/:fileId',
    element: withSuspense(<PDFPreviewOld />),
  },
  {
    path: '/preview/:envelopeId/:itemId',
    element: withSuspense(<PDFPreview />),
  },
  {
    path: '/:id/:itemId',
    element: withSuspense(<PDFEditorOld />),
  },
  {
    path: '/:envelopeId/:id/:itemId',
    element: withSuspense(<PDFEditor />),
  },
  {
    path: '/template/:templateId/:id/:itemId',
    element: withSuspense(<TemplateEditor />),
  },
  {
    path: '/integration/auth/error',
    element: withSuspense(<IntegrationErrorPage />),
  },
  {
    path: '/',
    element: withSuspense(<EditorHeader showButtons={false} />),
  },
  {
    path: '/pdf-editor/:envelopeId/:itemId/:fileId/',
    element: withSuspense(<PDFEditorV3 />),
  },
  {
    path: '/word-editor/:envelopeId/:itemId/:fileId/',
    element: withSuspense(<SuperDocEditorV3 />),
  },
  {
    path: '/pdf-sign/:itemId/:fileHistoryId/',
    element: withSuspense(<SignDocs />),
  },
  {
    path: '/pdf-preview/:envelopeId/:itemId/:fileId/',
    element: withSuspense(<PDFPreviewV3 />),
  },
  {
    path: '/template/v3/:templateId/:fileId/:itemId/',
    element: withSuspense(<TemplateEditorV3 />),
  },
  {
    path: '/template/v3/board/:templateId/:fileId/',
    element: withSuspense(<TemplateEditorV3 />),
  },
  {
    path: '/download/v3/:fileHistoryId/:fileId',
    element: withSuspense(<DownloadV3 />),
  },
  {
    path: '/form-editor/:envelopeId',
    element: withSuspense(<FormEditor />),
  },
  {
    path: '/superadmin',
    element: withSuspense(<SuperAdminLayout />),
    children: [
      {
        index: true,
        element: withSuspense(<SuperAdminAccountsList />),
      },
      {
        path: 'accounts/:accountId',
        element: withSuspense(<SuperAdminAccountDetail />),
      },
      {
        path: 'accounts/:accountId/boards/:boardId',
        element: withSuspense(<SuperAdminBoardDetail />),
      },
    ],
  },
  // Dev-only routes
  ...(IS_DEV
    ? [
        {
          path: '/dev/icons',
          element: withSuspense(<IconGallery />),
        },
      ]
    : []),
];

export const router = createBrowserRouter([
  {
    element: <AppRoutes />,
    children: routes,
  },
]);
